import type {
  LinksFunction,
  LoaderFunctionArgs,
  MetaFunction,
} from "@remix-run/node";
import { redirect } from "@remix-run/node";
import { useState } from "react";

import styles from "./styles.module.css";

const APP_STORE_URL = "https://apps.shopify.com/matrix-fit-recommender";

export const meta: MetaFunction = () => [
  { title: "MatrixFit Recommender | Size recommendations for Shopify apparel stores" },
  {
    name: "description",
    content:
      "Turn your size chart into instant size recommendations on every product page. Shoppers enter two measurements, get their size, and stop returning the wrong one.",
  },
  { property: "og:title", content: "MatrixFit Recommender" },
  {
    property: "og:description",
    content:
      "Size recommendations for Shopify apparel stores, powered by your own fit data.",
  },
  { property: "og:image", content: "/landing/storefront-widget.png" },
];

export const links: LinksFunction = () => [
  {
    rel: "preload",
    href: "/landing/fonts/outfit-latin-wght.woff2",
    as: "font",
    type: "font/woff2",
    crossOrigin: "anonymous",
  },
];

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);

  if (url.searchParams.get("shop")) {
    throw redirect(`/app?${url.searchParams.toString()}`);
  }

  // Installation must begin from a Shopify-owned surface (App Store / Admin),
  // which always supplies the shop via the `shop` query param (handled above).
  // We intentionally do NOT render a manual "enter your myshopify.com domain"
  // form here — App Store requirement 2.3.1 prohibits manual shop entry during
  // installation.
  return null;
};

/* Sample denim chart used by the live demo. Mirrors the shape of a real
   uploaded matrix: waist x inseam -> size label. */
/* BODY measurement ranges (inches) -> garment size. The size labels are
   deliberately a different system (US size + length) than the inputs, so the
   demo shows a translation, not an echo: waist 32" -> size 10 / Regular. */
const DEMO_CHART = [
  { wMin: 27.0, wMax: 28.9, iMin: 29.0, iMax: 30.7, size: "4 / Regular" },
  { wMin: 29.0, wMax: 30.4, iMin: 29.0, iMax: 30.7, size: "6 / Regular" },
  { wMin: 30.5, wMax: 31.2, iMin: 29.0, iMax: 30.7, size: "8 / Regular" },
  { wMin: 31.3, wMax: 32.7, iMin: 29.0, iMax: 30.7, size: "10 / Regular" },
  { wMin: 31.3, wMax: 32.7, iMin: 30.8, iMax: 32.5, size: "10 / Long" },
  { wMin: 32.8, wMax: 34.3, iMin: 29.0, iMax: 30.7, size: "12 / Regular" },
];

type DemoResult =
  | { kind: "empty" }
  | { kind: "invalid" }
  | { kind: "outOfRange" }
  | { kind: "match"; size: string; confidence: number };

function recommend(waistRaw: string, inseamRaw: string, unit: "in" | "cm"): DemoResult {
  if (waistRaw.trim() === "" || inseamRaw.trim() === "") return { kind: "empty" };

  let waist = Number(waistRaw);
  let inseam = Number(inseamRaw);
  if (!Number.isFinite(waist) || !Number.isFinite(inseam)) return { kind: "invalid" };

  if (unit === "cm") {
    waist /= 2.54;
    inseam /= 2.54;
  }

  let best: { size: string; distance: number } | null = null;
  for (const row of DEMO_CHART) {
    if (waist >= row.wMin && waist <= row.wMax && inseam >= row.iMin && inseam <= row.iMax) {
      const distance = Math.hypot(
        waist - (row.wMin + row.wMax) / 2,
        inseam - (row.iMin + row.iMax) / 2,
      );
      if (!best || distance < best.distance) best = { size: row.size, distance };
    }
  }

  if (!best) return { kind: "outOfRange" };

  const confidence = Math.min(96, Math.max(80, Math.round(96 - best.distance * 6)));
  return { kind: "match", size: best.size, confidence };
}

function DemoWidget() {
  const [unit, setUnit] = useState<"in" | "cm">("in");
  const [waist, setWaist] = useState("");
  const [inseam, setInseam] = useState("");

  const result = recommend(waist, inseam, unit);

  return (
    <div className={styles.demo} aria-label="Live size finder demo">
      <div className={styles.demoHeader}>
        <span className={styles.demoTitle}>Find My Size</span>
        <div className={styles.demoUnits} role="group" aria-label="Measurement unit">
          <button
            type="button"
            className={unit === "in" ? styles.demoUnitActive : styles.demoUnit}
            onClick={() => setUnit("in")}
          >
            in
          </button>
          <button
            type="button"
            className={unit === "cm" ? styles.demoUnitActive : styles.demoUnit}
            onClick={() => setUnit("cm")}
          >
            cm
          </button>
        </div>
      </div>

      <div className={styles.demoInputs}>
        <label className={styles.demoField}>
          <span className={styles.demoLabel}>Waist</span>
          <input
            className={styles.demoInput}
            inputMode="decimal"
            placeholder={unit === "in" ? "32" : "81"}
            value={waist}
            onChange={(e) => setWaist(e.target.value)}
          />
        </label>
        <label className={styles.demoField}>
          <span className={styles.demoLabel}>Inseam</span>
          <input
            className={styles.demoInput}
            inputMode="decimal"
            placeholder={unit === "in" ? "30" : "76"}
            value={inseam}
            onChange={(e) => setInseam(e.target.value)}
          />
        </label>
      </div>

      <div className={styles.demoResult} role="status">
        {result.kind === "empty" && (
          <p className={styles.demoHint}>
            Enter both measurements to see a recommendation.
          </p>
        )}
        {result.kind === "invalid" && (
          <p className={styles.demoHint}>Measurements need to be numbers.</p>
        )}
        {result.kind === "outOfRange" && (
          <p className={styles.demoHint}>
            That is outside this sample chart. In your store, the widget uses
            your own chart's full range.
          </p>
        )}
        {result.kind === "match" && (
          <div className={styles.demoMatch}>
            <div>
              <span className={styles.demoMatchLabel}>Recommended size</span>
              <span className={styles.demoMatchSize}>{result.size}</span>
            </div>
            <span className={styles.demoConfidence}>
              {result.confidence}% confidence
            </span>
          </div>
        )}
      </div>

      <p className={styles.demoCaption}>
        A working copy of the storefront widget, using a sample denim chart.
      </p>
    </div>
  );
}

export default function App() {
  return (
    <div className={styles.page}>
      <header className={styles.nav}>
        <a href="/" className={styles.brand}>
          <img src="/app-icon.png" alt="" width="28" height="28" className={styles.brandIcon} />
          MatrixFit<span className={styles.brandAccent}>Recommender</span>
        </a>
        <a href={APP_STORE_URL} className={styles.navCta}>
          View on App Store
        </a>
      </header>

      <main>
        <section className={styles.hero}>
          <div className={styles.heroCopy}>
            <p className={styles.eyebrow}>Shopify app for apparel stores</p>
            <h1 className={styles.heading}>
              The right size, before they buy.
            </h1>
            <p className={styles.tagline}>
              Turn your size chart into instant recommendations on every
              product page. Shoppers stop guessing, returns stop piling up.
            </p>
            <a href={APP_STORE_URL} className={styles.primaryCta}>
              View on App Store
            </a>
          </div>
          <div className={styles.heroDemo}>
            <DemoWidget />
          </div>
        </section>

        <section className={styles.steps}>
          <h2 className={styles.sectionHeading}>How it works</h2>
          <ol className={styles.stepList}>
            <li className={styles.step}>
              <span className={styles.stepNumber}>1</span>
              <div>
                <h3 className={styles.stepTitle}>Upload your size chart</h3>
                <p className={styles.stepText}>
                  Import the CSV you already have. MatrixFit validates it and
                  stores it as a reusable size matrix.
                </p>
              </div>
            </li>
            <li className={styles.step}>
              <span className={styles.stepNumber}>2</span>
              <div>
                <h3 className={styles.stepTitle}>Assign it to products</h3>
                <p className={styles.stepText}>
                  Pick the products or collections each matrix covers. The
                  widget appears on those product pages automatically.
                </p>
              </div>
            </li>
            <li className={styles.step}>
              <span className={styles.stepNumber}>3</span>
              <div>
                <h3 className={styles.stepTitle}>Watch the fit data work</h3>
                <p className={styles.stepText}>
                  Shoppers enter two measurements and get a size with a
                  confidence score. Analytics shows which sizes convert.
                </p>
              </div>
            </li>
          </ol>
        </section>

        <section className={styles.proof}>
          <div className={styles.proofCopy}>
            <h2 className={styles.sectionHeading}>It lives on your product page</h2>
            <p className={styles.proofText}>
              No theme code edits. The widget installs as a theme app
              extension, matches your store's look, and pre-selects the
              recommended size on add to cart.
            </p>
          </div>
          <figure className={styles.browserFrame}>
            <div className={styles.browserBar}>
              <span className={styles.browserDot} />
              <span className={styles.browserDot} />
              <span className={styles.browserDot} />
            </div>
            <img
              src="/landing/storefront-widget.png"
              alt="A Shopify product page for slim fit jeans with the MatrixFit size finder open, translating waist and inseam measurements into size 10 / Regular at 85% confidence"
              width="1600"
              height="900"
              loading="lazy"
              className={styles.proofImage}
            />
          </figure>
        </section>

        <section className={styles.pricing}>
          <h2 className={styles.sectionHeading}>Pricing</h2>
          <p className={styles.pricingSub}>
            Start free. Upgrade when you need more than one size matrix.
          </p>
          <div className={styles.planGrid}>
            <div className={styles.plan}>
              <h3 className={styles.planName}>Free</h3>
              <p className={styles.planPrice}>
                $0<span className={styles.planPeriod}> forever</span>
              </p>
              <ul className={styles.planFeatures}>
                <li>1 size matrix</li>
                <li>Size finder widget</li>
                <li>7-day analytics</li>
                <li>Email support</li>
              </ul>
            </div>
            <div className={styles.planFeatured}>
              <h3 className={styles.planName}>Plus</h3>
              <p className={styles.planPrice}>
                $9.99<span className={styles.planPeriod}> / month</span>
              </p>
              <ul className={styles.planFeatures}>
                <li>Unlimited matrices</li>
                <li>90-day analytics</li>
                <li>Live chat support</li>
              </ul>
            </div>
          </div>
        </section>

        <section className={styles.finalCta}>
          <h2 className={styles.finalHeading}>Give your shoppers their size.</h2>
          <a href={APP_STORE_URL} className={styles.primaryCtaInverted}>
            View on App Store
          </a>
        </section>
      </main>

      <footer className={styles.footer}>
        <p className={styles.footerBrand}>MatrixFit Recommender, by AppScout Labs</p>
        <nav className={styles.footerLinks}>
          <a href="/privacy">Privacy</a>
          <a href="/terms">Terms</a>
          <a href="mailto:support@labs.appscout.io">Support</a>
        </nav>
      </footer>
    </div>
  );
}
